import { useQuery } from "@tanstack/react-query"
import { CheckCircle2 } from "lucide-react"
import { Karte, Leer } from "@/components/ui"
import { api, fehlertext } from "@/lib/api"
import { nf0 } from "@/lib/format"
import { alsMinSek, alsText, ampelAnfeuchtung, ampelGraedigkeit, type Ampel } from "@/lib/pruefung"
import type { Jetzt, ProbeAntwort } from "@/lib/terminalApi"

interface SchichtDaten {
  jetzt: Jetzt
  schicht_fenster: Array<{ zeitfenster: string; erfasst: boolean; faellig: boolean }>
  proben: ProbeAntwort[]
}

const PUNKT: Record<Exclude<Ampel, null>, { farbe: string; text: string }> = {
  gruen: { farbe: "#0ca30c", text: "im Soll" },
  gelb: { farbe: "#fab219", text: "knapp" },
  rot: { farbe: "#d03b3b", text: "außerhalb" },
}
const PRUEFUNG: Record<string, string> = {
  ok: "", ausreisser: "Ausreißer", grenzwert: "Grenzwert", unvollstaendig: "unvollständig", fehlzeile: "Fehlzeile",
}

function Ampelwerte({ p, praefix, soll, art }: { p: ProbeAntwort; praefix: string; soll: number | null; art: "anf" | "graed" }) {
  return (
    <span className="inline-flex gap-2">
      {[1, 2, 3].map((i) => {
        const v = p[`${praefix}_${i}`] as number | null
        if (v == null) return <span key={i} className="text-muted">–</span>
        const a = art === "anf" ? ampelAnfeuchtung(v, soll) : ampelGraedigkeit(v, soll)
        return (
          <span key={i} className="inline-flex items-center gap-1" title={a ? PUNKT[a].text : undefined}>
            {a && <i className="inline-block h-2 w-2 rounded-full" style={{ background: PUNKT[a].farbe }} />}
            {alsText(v)}
          </span>
        )
      })}
    </span>
  )
}

/** Laufende Schicht: was ist erfasst, was fehlt, wie liegen die Werte (aktualisiert jede Minute). */
export function SchichtPage() {
  const abfrage = useQuery({
    queryKey: ["schicht"],
    queryFn: async () => (await api.get<SchichtDaten>("/schicht/aktuell", { params: { stunden: 24 } })).data,
    refetchInterval: 60_000,
  })
  const d = abfrage.data
  const th = "px-2 py-1.5 text-left text-[11.5px] font-medium text-muted whitespace-nowrap"
  const td = "border-t border-grid px-2 py-1.5 whitespace-nowrap"
  return (
    <main className="mx-auto grid max-w-[1260px] gap-[18px] px-4 pb-12 pt-6">
      <div className="grid gap-1">
        <p className="m-0 font-mono text-[11.5px] font-medium uppercase tracking-[0.08em] text-muted">Konti-Verdrängung · Heinkel</p>
        <h1 className="m-0 text-[28px] font-semibold">Proben der laufenden Schicht</h1>
        {d && <p className="m-0 text-ink2">{`${d.jetzt.schicht}schicht am ${new Date(d.jetzt.datum).toLocaleDateString("de-DE")} · jetzt ${d.jetzt.uhrzeit} Uhr`}</p>}
      </div>
      {abfrage.isError && <Leer>{fehlertext(abfrage.error, "Die Schicht konnte nicht geladen werden.")}</Leer>}
      {d && (
        <>
          <section aria-label="Proben der Schicht" className="grid grid-cols-2 gap-3 md:grid-cols-4">
            {d.schicht_fenster.map((f) => (
              <div key={f.zeitfenster} className="grid gap-1 rounded-[10px] border border-hair/10 bg-surface px-4 py-3">
                <span className="text-[12.5px] text-ink2">{`Probe ${f.zeitfenster} Uhr`}</span>
                <span className="inline-flex items-center gap-2 text-lg font-semibold">
                  {f.erfasst ? (
                    <><CheckCircle2 className="h-5 w-5 text-good" aria-hidden="true" /> erfasst</>
                  ) : f.faellig ? (
                    <><i className="inline-block h-3 w-3 rounded-full bg-warn" aria-hidden="true" /> fehlt</>
                  ) : (
                    <span className="text-muted">noch nicht fällig</span>
                  )}
                </span>
              </div>
            ))}
          </section>
          <Karte titel="Proben der letzten 24 Stunden" unter="Neueste zuerst. Punkte zeigen die Toleranz-Ampel wie im Tagebuch: Anfeuchtung ±0,9 grün / ±2 gelb, Grädigkeit unter Soll rot.">
            {d.proben.length === 0 ? <Leer>In den letzten 24 Stunden wurde nichts erfasst.</Leer> : (
              <div className="min-w-0 overflow-x-auto">
                <table className="tabular w-full border-collapse text-[12.5px]">
                  <thead>
                    <tr>{["Tag", "Fenster", "Mischung", "NC-Typ", "Gebinde", "Medium", "Anfeuchtung Ist", "Grädigkeit Ist", "Zyklus", "Auswurf", "Durchsatz", "Prüfung", "Erfasst"].map((h) => <th key={h} className={th}>{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {d.proben.map((p) => (
                      <tr key={p.id}>
                        <td className={td}>{new Date(p.datum).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" })}</td>
                        <td className={td}>{p.zeitfenster}</td>
                        <td className={`${td} font-mono text-xs`}>{p.mischung}</td>
                        <td className={`${td} font-mono text-xs`}>{p.nc_typ}</td>
                        <td className={td}>{p.gebinde}</td>
                        <td className={td}>{p.medium}</td>
                        <td className={td}><Ampelwerte p={p} praefix="anf_ist" soll={p.anf_soll as number | null} art="anf" /></td>
                        <td className={td}><Ampelwerte p={p} praefix="graed_ist" soll={p.graed_soll as number | null} art="graed" /></td>
                        <td className={td}>{alsMinSek(p.zykluszeit_s as number | null)}</td>
                        <td className={td}>{p.auswurfgewicht_kg != null ? `${alsText(p.auswurfgewicht_kg as number)} kg` : "–"}</td>
                        <td className={td}>{p.durchsatz_kg_h != null ? `${nf0.format(p.durchsatz_kg_h)} kg/h` : "–"}</td>
                        <td className={td}>{PRUEFUNG[p.pruefung] && <span className="rounded-full bg-surface2 px-2 py-0.5 text-[11.5px]">{PRUEFUNG[p.pruefung]}</span>}</td>
                        <td className={`${td} text-muted`}>{p.quelle === "terminal" ? `${p.erfasst_von ?? ""} (Terminal)` : "Excel-Tagebuch"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Karte>
        </>
      )}
    </main>
  )
}
